import { Injectable, inject } from '@angular/core';
import { IFilterService } from 'angular';

import { HelpersService } from '../helpers/helpers.service';
import { FILTER_PORT } from '../utils';
import { PosConfigImagesService } from './images/pos-config-images.service';
import { PosConfigNavigationService } from './navigation/pos-config-navigation.service';
import { PosConfigOptionsService } from './options/pos-config-options.service';
import { PosConfigPagesService } from './pages/pos-config-pages.service';
import { PosConfigSettingsService } from './settings/pos-config-settings.service';
import { PosConfigTilesService } from './tiles/pos-config-tiles.service';

/**
 * PosConfigService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/posConfig/posConfig.service.js
 * (legacy AngularJS name 'PosConfigService'). Orchestrates the six POS config
 * sub-services and exposes them as properties (`options`/`images`/`tiles`/
 * `pages`/`navigation`/`settings`) the same way the legacy service did, so
 * consumers like `PosConfigService.pages.getModel()` keep working.
 *
 * Dependency mapping:
 *  - The six sub-services are Angular 18 and only ever reached through this
 *    parent, so they are injected directly and NOT downgrade-registered.
 *  - HelpersService → Angular 18 (inject); `$filter` → FILTER_PORT.
 *  - jQuery `$.extend(target, obj)` → `Object.assign(target, obj)` (shallow
 *    merge mutating + returning target — identical semantics here).
 *
 * Registered back into AngularJS under 'PosConfigService' via downgradeInjectable
 * in app/upgrade/ng-downgraded-components.ts. Wave 3 #17.
 */
@Injectable({ providedIn: 'root' })
export class PosConfigService {
  options = inject(PosConfigOptionsService);
  images = inject(PosConfigImagesService);
  tiles = inject(PosConfigTilesService);
  pages = inject(PosConfigPagesService);
  navigation = inject(PosConfigNavigationService);
  settings = inject(PosConfigSettingsService);

  private helpersService = inject(HelpersService);
  private $filter = inject<IFilterService>(FILTER_PORT);

  init(): boolean {
    return true;
  }

  /**
   * Updates tile for specific page assumes tiles are already populated in PosConfigTilesService
   *
   * @param pageId {String}
   * @param tile {Tile}
   *
   * @returns {*} {Tile}
   */
  updateTile(pageId: any, tile: any): any {
    const page = this.pages.findPage(pageId).page;

    if (this.tiles.findTile(tile.config.id)) {
      this.tiles.setTile(tile);
    } else {
      this.tiles.addTile(tile);
    }

    page.items = this.tiles.getModel();

    this.pages.updatePage(page);

    return this.tiles.findTile(tile.config.id).tile;
  }

  updateNavigationTiles(pageId: any): void {
    let i, ii;
    let j, jj;
    let page;
    const found = this.pages.findPage(pageId);
    const pages = this.pages.getModel();

    if (found) {
      // Update the tiles with new page name
      page = found.page;

      for (i = 0, ii = pages.length; i < ii; i++) {
        for (j = 0, jj = pages[i].items.length; j < jj; j++) {
          if (pages[i].items[j].config.type === 'navigation' && pages[i].items[j].config.pageId === page.pageId) {
            pages[i].items[j].name = page.pageHeader;
            pages[i].items[j].pageNumber = page.pageNumber;
          }
        }
        this.pages.updatePage(pages[i]);
      }
    }
  }

  relinkNavigationTilesAfterPageDeletion(): void {
    let i, ii;
    let j, jj;
    let k, kk;
    const pages = this.pages.getModel();

    for (i = 0, ii = pages.length; i < ii; i++) {
      for (j = 0, jj = pages[i].items.length; j < jj; j++) {
        for (k = 0, kk = pages.length; k < kk; k++) {
          if (pages[k].pageId === pages[i].items[j].config.pageId) {
            pages[i].items[j].pageNumber = pages[k].pageNumber;
            this.pages.updatePage(pages[i]);
          }
        }
      }
    }
  }

  getReferencedNavigationTiles(pageId: any): any[] {
    let i, ii;
    let j, jj;
    const r: any[] = [];
    const pages = this.pages.getModel();

    for (i = 0, ii = pages.length; i < ii; i++) {
      for (j = 0, jj = pages[i].items.length; j < jj; j++) {
        if (pages[i].items[j].config.type === 'navigation' && pages[i].items[j].config.pageId === pageId) {
          r.push({
            page: pages[i],
            tile: pages[i].items[j],
          });
        }
      }
    }

    return r;
  }

  getRemovedPageTiles(): any[] {
    let i, ii;
    let j, jj;
    const r: any[] = [];
    const ids: any[] = [];
    const pages = this.pages.getModel();

    for (i = 0, ii = pages.length; i < ii; i++) {
      ids.push(pages[i].pageId);
    }

    for (i = 0, ii = pages.length; i < ii; i++) {
      for (j = 0, jj = pages[i].items.length; j < jj; j++) {
        if (pages[i].items[j].config.type === 'navigation' && ids.indexOf(pages[i].items[j].config.pageId) === -1) {
          // Page no longer exists remove the tile
          r.push({
            page: pages[i],
            tile: pages[i].items[j],
          });
        }
      }
    }

    return r;
  }

  updateRemovedPageTiles(remove: any[]): void {
    let i, ii;

    for (i = 0, ii = remove.length; i < ii; i++) {
      this.pages.removePageTile(remove[i].page.pageId, remove[i].tile);
      this.pages.updatePage(remove[i].page);
    }
  }

  getConfig(process?: boolean): any {
    let displayItems = this.pages.getModel();

    if (process) {
      displayItems = this.process(this.pages.getModel());
    }

    return Object.assign(this.options.getModel(), {
      splash: this.images.getModel(),
      settings: this.settings.getModel(),
      displayItems: displayItems,
      headerButtonConfig: this.navigation.getModel(),
    });
  }

  setConfig(config: any): void {
    if (config) {
      this.options.setModel(config);
      if (config.splash) {
        if (config.splash.images === null && config.splash.displayTime === null) {
          config.splash.images = [];
          config.splash.displayTime = 5;
        }
        this.images.setModel(config.splash);
      }

      if (config.headerButtonConfig instanceof Array && config.headerButtonConfig.length) {
        this.navigation.setModel(config.headerButtonConfig);
      }

      if (config.settings) {
        if (Object.prototype.hasOwnProperty.call(config.settings, 'enableCustomerInputOnPurchase')) {
          this.settings.setModel(config.settings);
        }
      }

      if (config.displayItems instanceof Array && config.displayItems.length) {
        this.pages.setModel(config.displayItems);
      } else {
        this.pages.setModel([this.pages.create()]);
      }
    }
  }

  process(displayItems: any[]): any[] {
    let i, ii;
    let j, jj;
    let items;

    console.log(displayItems);

    for (i = 0, ii = displayItems.length; i < ii; i++) {
      items = [];
      for (j = 0, jj = displayItems[i].items.length; j < jj; j++) {
        if (displayItems[i].items[j].config.type !== 'tile') {
          items.push(displayItems[i].items[j]);
        }
      }
      displayItems[i].items = items;
    }

    return displayItems;
  }

  /**
   * Parses yyyy-MM-dd and returns Date Object
   *
   * @param date {String}
   * @returns {Date}
   */
  parsePresellDate(date: string): Date {
    const s = date.split('-');
    const d = new Date(Number(s[0]), parseInt(s[1], 10) - 1, Number(s[2]), 12, 0, 0, 0);

    return d;
  }

  /**
   * Returns Date Object as yyyy-MM-dd
   *
   * @param date {Date}
   * @returns {String}
   */
  dateToPresellDate(date: any): any {
    let r: any = new Date();

    if (date instanceof Date) {
      r = this.$filter('date')(date, this.helpersService.dateFormats.fieldDate);
    }

    return r;
  }

  /**
   * Returns yyyy-MM-dd as M/d/yy
   * If date is empty returns current date as M/d/yy
   *
   * @param date {String}
   * @returns {String}
   */
  presellDateToTileDate(date: any): any {
    let r: any = new Date();

    if (date) {
      r = this.$filter('date')(this.parsePresellDate(date), this.helpersService.dateFormats.shortDate);
    } else {
      r = this.$filter('date')(r, this.helpersService.dateFormats.shortDate);
    }

    return r;
  }

  reset(): void {
    this.options.setModel(false);
    this.images.setModel(false);
    this.tiles.setModel(false);
    this.pages.setModel(false);
    this.navigation.setModel(false);
    this.settings.setModel(false);
  }

  /**
   * Returns items enabled for pos only
   * @param list {Array}
   * @returns Array
   */
  processPricesBySalesChannel(list: any): any[] {
    const r: any[] = [];
    let i, ii, j, jj;
    const channel = 'pointOfSale';

    if (list instanceof Array) {
      for (i = 0, ii = list.length; i < ii; i++) {
        if (list[i].salesChannels instanceof Array) {
          for (j = 0, jj = list[i].salesChannels.length; j < jj; j++) {
            if (
              list[i].salesChannels[j].channel &&
              list[i].salesChannels[j].channel === channel &&
              list[i].salesChannels[j].enabled
            ) {
              r.push(list[i]);
            }
          }
        }
      }
    }

    return r;
  }
}
